import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { signUp } from "../../store/session";
import { fetchUsersAdmin } from "../../store/users";
import { useHistory } from 'react-router-dom';
import { refreshCSRFToken } from "../../utils/csrfFetch";
import { useLoadAppData } from "../../hooks/useLoadAppData";

function SignupFormPage() {
  const dispatch = useDispatch();
  const sessionUser = useSelector((state) => state.session.user);
  const accountsFromStore = useSelector((state) => Array.isArray(state.accounts) ? state.accounts : Object.values(state.accounts) || []);
  const history = useHistory();
  const isLoading = useLoadAppData();

  const [formData, setFormData] = useState({
    alpha_code: "",
    email: "",
    driver_number: "",
    account_id: sessionUser?.is_admin ? "" : sessionUser?.account_id,
    is_admin: false,
    is_manager: false,
    is_account_manager: false,
    is_road_driver: false,
    is_yard_driver: false,
    is_mechanic: false,
    is_security: false,
    is_super_manager: false,
  });
  const [errors, setErrors] = useState([]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "radio" && name === "role") {
      // Reset all roles first
      const resetRoles = Object.keys(formData)
        .filter(key => key.startsWith('is_'))
        .reduce((acc, key) => ({ ...acc, [key]: false }), {});

      // Set the selected role to true
      setFormData(prevState => ({
        ...prevState,
        ...resetRoles,
        [value]: true  // value should be the role name (e.g., "is_admin")
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        // Convert alpha_code to uppercase, email to lowercase, leave other fields unchanged
        [name]: name === 'alpha_code'
          ? value.toUpperCase()
          : name === 'email'
          ? value.toLowerCase()
          : type === 'checkbox'
          ? checked
          : value
      }));
    }
  };

  const shouldShowDriverNumber = () => {
    // Only show driver number if ONLY these roles are selected
    const driverRoles = ['is_road_driver', 'is_yard_driver', 'is_mechanic'];
    const selectedRoles = Object.entries(formData)
      .filter(([key, value]) => key.startsWith('is_') && value === true)
      .map(([key]) => key);

    return selectedRoles.some(role => driverRoles.includes(role)) &&
           selectedRoles.every(role => driverRoles.includes(role));
  };

  const getUserRoleLevel = (user) => {
    if (user.is_admin) return 5;
    if (user.is_super_manager) return 4;
    if (user.is_account_manager) return 3;
    if (user.is_manager) return 2;
    return 1; // for all other roles
  };

  const getAvailableRoles = () => {
    const userLevel = getUserRoleLevel(sessionUser);
    const roles = [
      { name: 'is_admin', label: 'Admin', level: 5 },
      { name: 'is_super_manager', label: 'Super Manager', level: 4 },
      { name: 'is_account_manager', label: 'Account Manager', level: 3 },
      { name: 'is_manager', label: 'Manager', level: 2 },
      { name: 'is_road_driver', label: 'Road Driver', level: 1 },
      { name: 'is_yard_driver', label: 'Yard Driver', level: 1 },
      { name: 'is_mechanic', label: 'Mechanic', level: 1 },
      { name: 'is_security', label: 'Security', level: 1 },
    ];

    return sessionUser.is_admin ? roles : roles.filter(role => role.level < userLevel);
  };

  const shouldShowAlphaCode = () => {
    return !formData.is_security;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Starting form submission...');
    const validationErrors = [];

    // Validate role selection
    const hasRoleSelected = Object.entries(formData)
      .filter(([key]) => key.startsWith('is_'))
      .some(([_, value]) => value);

    if (!hasRoleSelected) {
      validationErrors.push('Please select a role');
    }

    // Validate alpha code (required for non-security roles)
    if (!formData.is_security && (!formData.alpha_code || formData.alpha_code.trim() === '')) {
      validationErrors.push('Alpha code is required for non-security roles');
    }

    // Validate account selection - only required for non-admin users
    if (sessionUser.is_admin && !formData.is_admin && (!formData.account_id || formData.account_id === "")) {
      validationErrors.push('Please select an account for non-admin users');
    }

    // Handle driver number validation and conversion
    let driverNumber = null;
    if (shouldShowDriverNumber()) {
      if (!formData.driver_number || formData.driver_number.trim() === '') {
        validationErrors.push('Driver number is required for Road Driver, Yard Driver, or Mechanic roles');
      } else {
        driverNumber = parseInt(formData.driver_number.trim(), 10);
        if (isNaN(driverNumber)) {
          validationErrors.push('Driver number must be a valid number');
        }
      }
    }

    if (validationErrors.length > 0) {
      console.log('Frontend validation errors:', validationErrors);
      setErrors(validationErrors);
      return;
    }

    // Create data object with proper driver_number handling
    const userData = {
      ...formData,
      driver_number: driverNumber,
      account_id: formData.is_admin ? null : formData.account_id,
      password: "Welcome1"
    };

    console.log('Prepared user data:', userData);

    try {
      console.log('Refreshing CSRF token...');
      await refreshCSRFToken();
      console.log('CSRF token refreshed, dispatching signup...');

      const data = await dispatch(signUp(userData));
      if (data) {
        // Check if the error is about duplicate email or alpha code
        if (data.includes('Email address is already in use')) {
          setErrors(['This email is already registered']);
        } else if (data.includes('Alpha code is already in use')) {
          setErrors(['This alpha code is already in use']);
        } else {
          setErrors(data);
        }
      } else {
        history.push('/');
      }
    } catch (error) {
      console.error('Error during signup:', error);
      setErrors(['An unexpected error occurred. Please try again.']);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          <div className="ml-4 text-2xl text-blue-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 min-h-[calc(100vh-100px)]">
      <h1 className="text-8xl font-bold mb-10 text-center text-blue-600">New User</h1>

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
        {errors.length > 0 && (
          <div className="mb-6 bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
            <h3 className="text-2xl font-semibold text-red-700 mb-2">Please fix the following errors:</h3>
            {errors.map((error, idx) => (
              <p key={idx} className="text-red-600 text-xl flex items-center">
                <span className="mr-2">•</span>
                {error}
              </p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column - Role Selection */}
            <div>
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Select Role <span className="text-red-500">*</span></h2>
              <div className="space-y-4 bg-gray-50 p-6 rounded-lg">
                {getAvailableRoles().map(role => (
                  <div key={role.name} className="flex items-center bg-white p-3 rounded-lg shadow-sm">
                    <input
                      type="radio"
                      id={role.name}
                      name="role"
                      value={role.name}
                      checked={formData[role.name]}
                      onChange={handleChange}
                      className="h-6 w-6 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor={role.name} className="ml-8 text-2xl text-gray-700">
                      {role.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - User Information */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">User Information</h2>

              <div className="space-y-4">
                {sessionUser?.is_admin && !formData.is_admin && (
                  <div>
                    <label htmlFor="account-select" className="block text-2xl font-medium text-gray-700 mb-2">
                      Select Account <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="account-select"
                      name="account_id"
                      value={formData.account_id || ''}
                      onChange={(e) => {
                        const selectedId = parseInt(e.target.value, 10);
                        if (!isNaN(selectedId)) {
                          setFormData(prev => ({
                            ...prev,
                            account_id: selectedId
                          }));
                        }
                      }}
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      required
                    >
                      <option value="">Select an account</option>
                      {accountsFromStore.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.account_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {shouldShowAlphaCode() && (
                  <div>
                    <label htmlFor="alpha-code" className="block text-2xl font-medium text-gray-700 mb-2">
                      Alpha Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="alpha-code"
                      type="text"
                      name="alpha_code"
                      value={formData.alpha_code}
                      onChange={handleChange}
                      required={!formData.is_security}
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="email-input" className="block text-2xl font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email-input"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                {shouldShowDriverNumber() && (
                  <div>
                    <label htmlFor="driver-number" className="block text-2xl font-medium text-gray-700 mb-2">
                      Driver Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="driver-number"
                      type="text"
                      name="driver_number"
                      value={formData.driver_number}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                )}

                <div className="mt-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
                  <p className="text-lg text-blue-700">
                    New users will be assigned a default password of "Welcome1" and will be prompted to change it on first login.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Buttons */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <button id='cancel-user-button'
              type="button"
              onClick={() => history.push('/')}
              className="px-6 py-3 text-2xl text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-all duration-200 w-1/4 focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Cancel
            </button>
            <button id='add-user-button'
              type="submit"
              className="px-6 py-3 text-2xl text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 w-1/4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SignupFormPage;
